import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import Zone from "@insite/client-framework/Components/Zone";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import CookiePrivacyPolicyPopup from "@insite/content-library/Components/CookiePrivacyPolicyPopup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { isGlobalViewsWebsite, isMgbwWebsite } from "@tSurya/Common/Utilities/websiteUtils";
import * as React from "react";
import { css } from "styled-components";

export interface FooterStyles {
    page?: MobiusPageProps;
}

// Keeps the footer scrollable clear of the floating Back to Top button on the MGBW PDP on mobile (see BackToTop.tsx).
const MOBILE_BACK_TO_TOP_CLEARANCE = "120px";

export const footerStyles: FooterStyles = {
    page: {
        css: css`
            max-width: 100% !important;
            padding: 0px !important;
            &[data-back-to-top-clearance="true"] {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding-bottom: ${MOBILE_BACK_TO_TOP_CLEARANCE} !important;
                        `,
                        css`
                            padding-bottom: ${MOBILE_BACK_TO_TOP_CLEARANCE} !important;
                        `,
                        css`
                            padding-bottom: ${MOBILE_BACK_TO_TOP_CLEARANCE} !important;
                        `,
                        null,
                        null,
                    ])}
            }
            .footer-link-list {
                > div[class^="GridOffset"] {
                    justify-content: space-between;
                    margin: 0;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                width: 100%;
                            `,
                            css`
                                width: 100%;
                            `,
                            null,
                            null,
                            null,
                        ])}
                    > div[class^="GridItemStyle-sc"] {
                        &:first-of-type {
                            ${({ theme }: { theme: BaseTheme }) =>
                                breakpointMediaQueries(theme, [
                                    css`
                                        padding-top: 0;
                                    `,
                                    css`
                                        padding-top: 0;
                                    `,
                                    css`
                                        padding-top: 16px;
                                    `,
                                    null,
                                    null,
                                ])}
                        }
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    padding-top: 8px;
                                    padding-left: 0;
                                    padding-right: 0;
                                    flex-basis: 100% !important;
                                    max-width: 100% !important;
                                `,
                                css`
                                    padding-top: 8px;
                                    padding-left: 0;
                                    padding-right: 0;
                                    flex-basis: 100% !important;
                                    max-width: 100% !important;
                                `,
                                css`
                                    padding-left: 0;
                                    padding-right: 0;
                                    padding-top: 16px;
                                `,
                                null,
                                null,
                            ])}
                        > div[class^="Wrapper-sc"] {
                            > div {
                                margin-top: 0;
                            }
                        }
                    }
                }
            }
            div[class^="GridItemStyle-sc"] {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            flex-basis: 100% !important;
                            max-width: 100% !important;

                            ${!isGlobalViewsWebsite() ? `
                                &:last-child {
                                    &:has(.social-links) {
                                        margin-top: 25px;
                                    }
                                }
                            ` : ''}
                        `,
                        css`
                            flex-basis: 100% !important;
                            max-width: 100% !important;
                            
                            ${!isGlobalViewsWebsite() ? `
                                &:last-child {
                                    &:has(.social-links) {
                                        margin-top: 25px;
                                    }
                                }
                            ` : ''}
                        `,
                        null,
                        null,
                        null,
                    ])}
            }
        `,
    },
};
const styles = footerStyles;

const Footer = ({ id }: PageProps) => {
    const isProductDetailsPage = useAppSelector(state => getCurrentPage(state).type === "ProductDetailsPage");

    return (
        <Page
            as="footer"
            isFooter={true}
            data-back-to-top-clearance={isMgbwWebsite() && isProductDetailsPage}
            {...styles.page}
        >
            <Zone contentId={id} zoneName="Content" />
            <CookiePrivacyPolicyPopup />
        </Page>
    );
};

const pageModule: PageModule = {
    component: Footer,
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "Footer" instead of this constant.
 */
export const FooterContext = "Footer";